import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import crmApi, { money, DOC_STATUS_META, fmtDate } from '../../utils/crmApi';
import useLiveRefresh from '../../utils/useLiveRefresh';

// Shared list used by Quotes, Service Orders and Invoices.
export default function CrmDocumentsList({ docType, title, subtitle, routeBase, createLabel }) {
  const { isAdmin } = useOutletContext();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [delDoc, setDelDoc] = useState(null);
  const [delErr, setDelErr] = useState(null);
  const nowY = new Date().getFullYear();
  const [year, setYear] = useState('all');
  const navigate = useNavigate();

  // `silent` background refreshes don't toggle the loading state, so the table
  // updates in place without blinking. Only the initial load shows "Loading…".
  function load(silent) {
    if (!silent) setLoading(true);
    const yq = year === 'all' ? '' : `&year=${year}`;
    crmApi.get(`/documents?doc_type=${docType}${yq}`).then(setDocs).finally(() => { if (!silent) setLoading(false); });
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [docType, year]);
  useLiveRefresh(() => load(true));

  const filtered = filter === 'all' ? docs : docs.filter(d => d.status === filter);
  const statuses = ['all', ...Array.from(new Set(docs.map(d => d.status)))];


  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: '800' }}>{title}</h1>
          <p style={{ fontSize: '13px', color: 'var(--muted)' }}>{subtitle}</p>
        </div>
        <select className="select" style={{ width: 120, marginLeft: 'auto', marginRight: '10px' }} value={year} onChange={e => setYear(e.target.value)}>
          <option value="all">All years</option>
          {Array.from({ length: (2035 - 2022) + 1 }, (_, i) => 2035 - i).map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button className="btn btn-primary" onClick={() => navigate(`/crm/${routeBase}/new`)}>+ {createLabel}</button>
      </div>

      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {statuses.map(s => (
          <button key={s} onClick={() => setFilter(s)}
            style={{ fontSize: '11px', fontWeight: '700', padding: '5px 12px', borderRadius: '99px', cursor: 'pointer',
              border: '1px solid var(--border)',
              background: filter === s ? 'var(--text)' : 'var(--surface)',
              color: filter === s ? '#fff' : 'var(--muted)', textTransform: 'capitalize' }}>
            {s === 'all' ? 'All' : (DOC_STATUS_META[s]?.label || s)}
          </button>
        ))}
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isAdmin ? '1.2fr 2fr 1fr 1fr 1.1fr 0.5fr' : '1.2fr 2fr 1fr 1fr 1.1fr', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
          {['Number', 'Account', 'Total', 'Status', 'Issue Date', ...(isAdmin ? [''] : [])].map((h, hi) => (
            <div key={hi} style={{ padding: '10px 16px', fontSize: '11px', fontWeight: '700', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.6px' }}>{h}</div>
          ))}
        </div>
        {loading ? <div style={{ padding: '20px', color: 'var(--muted)' }}>Loading…</div> :
          filtered.length === 0 ? <div style={{ padding: '24px', textAlign: 'center', color: 'var(--muted)', fontSize: '13px' }}>Nothing here yet.</div> :
            filtered.map((d, i) => {
              const sm = DOC_STATUS_META[d.status] || DOC_STATUS_META.draft;
              return (
                <div key={d.id} onClick={() => navigate(`/crm/${routeBase}/${d.id}`)}
                  style={{ display: 'grid', gridTemplateColumns: isAdmin ? '1.2fr 2fr 1fr 1fr 1.1fr 0.5fr' : '1.2fr 2fr 1fr 1fr 1.1fr', borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none', alignItems: 'center', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '700', fontFamily: 'var(--font-mono)' }}>{d.doc_number}</div>
                  <div style={{ padding: '12px 16px', fontSize: '13px' }}>{d.account_name || <span style={{ color: 'var(--muted)' }}>—</span>}</div>
                  <div style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '700' }}>{money(d.total, d.currency)}</div>
                  <div style={{ padding: '12px 16px' }}><span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 9px', borderRadius: '99px', background: sm.bg, color: sm.color }}>{sm.label}</span></div>
                  <div style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--muted2)' }}>{fmtDate(d.issue_date)}</div>
                  {isAdmin && <div style={{ padding: '12px 16px' }}>
                    <button title="Delete" onClick={e => { e.stopPropagation(); setDelErr(null); setDelDoc(d); }}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#DC2626', fontSize: '14px' }}>🗑</button>
                  </div>}
                </div>
              );
            })}
      </div>

      {delDoc && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setDelDoc(null); }}>
          <div className="modal" style={{ width: '420px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '800', marginBottom: '8px' }}>Delete {delDoc.doc_number}?</h3>
            <p style={{ fontSize: '13px', color: 'var(--muted2)', lineHeight: 1.6, marginBottom: '12px' }}>
              This permanently deletes the document and its line items{delDoc.stock_committed ? ', and returns its committed stock to inventory' : ''}. Dashboards and forecasts update immediately. This cannot be undone.
            </p>
            {delErr && <p style={{ fontSize: '12px', color: '#DC2626', marginBottom: '10px' }}>{delErr}</p>}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setDelDoc(null)}>Cancel</button>
              <button className="btn" style={{ background: '#DC2626', color: '#fff', borderColor: '#DC2626' }}
                onClick={async () => { try { await crmApi.delete(`/documents/${delDoc.id}`); setDelDoc(null); load(); } catch (e) { setDelErr(e?.error || 'Could not delete'); } }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const CrmQuotes = () => <CrmDocumentsList docType="quote" title="Quotes" subtitle="Price quotes for customers. Convert to a service order or invoice when accepted." routeBase="quotes" createLabel="New Quote" />;
export const CrmSalesOrders = () => <CrmDocumentsList docType="sales_order" title="Service Orders" subtitle="Confirmed orders ready to fulfil and invoice." routeBase="sales-orders" createLabel="New Service Order" />;
export const CrmInvoices = () => <CrmDocumentsList docType="invoice" title="Invoices" subtitle="Invoices go to the salesperson for approval, then to the customer." routeBase="invoices" createLabel="New Invoice" />;
